/**
 * ResultMethodNote.jsx — "How this was calculated" disclosure for primary results.
 *
 * Replaces prominent "Expected vs Sampled" toggles with a small note + an
 * optional inline expansion. Keeps user attention on the result; mechanics
 * are still one click away.
 *
 * Modes:
 *   - 'uncertainty_aware'  : probability-weighted aggregate is shown
 *   - 'sampled'            : single sampled response per individual is shown
 */
import { useState } from 'react';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

const COMPACT_COPY = {
  uncertainty_aware: 'This result accounts for response uncertainty.',
  sampled: 'This result is based on one simulated response per individual.',
};

const EXPANDED_COPY = {
  uncertainty_aware:
    'For each individual, SynPop estimates likelihoods across all response options. Aggregate charts use those likelihoods directly. Individual-level exports include one reproducible simulated response per person.',
  sampled:
    'Each individual produced one simulated response. Aggregate charts count those responses directly. This run did not produce probability vectors.',
};

export default function ResultMethodNote({ mode = 'uncertainty_aware', className, testId = 'result-method-note' }) {
  const [open, setOpen] = useState(false);
  const compact = COMPACT_COPY[mode] || COMPACT_COPY.uncertainty_aware;
  const expanded = EXPANDED_COPY[mode] || EXPANDED_COPY.uncertainty_aware;

  return (
    <div
      className={cn(
        'flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400',
        className
      )}
      data-testid={testId}
    >
      <Info className="w-3 h-3 mt-0.5 text-slate-400 flex-shrink-0" />
      <div className="flex-1 leading-relaxed">
        <span>{compact}</span>{' '}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 underline-offset-2 hover:underline transition-colors inline-flex items-center gap-0.5"
          data-testid={`${testId}-toggle`}
        >
          How this was calculated
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {open && (
          <p className="mt-1.5 text-slate-500 dark:text-slate-400" data-testid={`${testId}-expanded`}>
            {expanded}
          </p>
        )}
      </div>
    </div>
  );
}
